import { Box } from "@repo/components/src/ui/box";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import {
  Progress,
  ProgressFilledTrack,
} from "@repo/components/src/ui/progress";
import { Spinner } from "@repo/components/src/ui/spinner";
import { useEvent } from "expo";
import { useVideoPlayer, VideoView } from "expo-video";
import { Pause, Play } from "lucide-react-native";
import { useCallback, useEffect, useRef, useState } from "react";
import { MediaViewerProps } from "../types";

const VideoContent = (props: MediaViewerProps) => {
  const durationRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const postContent = props.postContent;
  const videoFile = postContent.content;
  const player = useVideoPlayer(
    {
      uri: videoFile?.url,
      useCaching: true,
    },
    (p) => {
      p.loop = false;
      p.timeUpdateEventInterval = 1;
    },
  );
  const { isPlaying } = useEvent(player, "playingChange", {
    isPlaying: player.playing,
  });
  const { status } = useEvent(player, "statusChange", {
    status: player.status,
  });
  const { currentTime } = useEvent(player, "timeUpdate", {
    currentTime: player.currentTime,
    bufferedPosition: player.bufferedPosition,
    currentLiveTimestamp: player.currentLiveTimestamp,
    currentOffsetFromLive: player.currentOffsetFromLive,
  });
  useEffect(() => {
    return () => {
      durationRef.current && clearTimeout(durationRef.current);
    };
  }, []);
  const [duration, setDuration] = useState(0);
  const onPlayPause = useCallback(() => {
    if (!videoFile?.url) {
      return;
    }
    if (player.playing) {
      player.pause();
    } else {
      player.play();
    }
    if (duration === 0) {
      durationRef.current = setTimeout(() => setDuration(player.duration), 500);
    }
  }, [player, videoFile, duration]);
  return (
    <>
      <VideoView
        nativeControls={false}
        crossOrigin="anonymous"
        player={player}
        fullscreenOptions={{ enable: false }}
        className="h-full"
        contentFit="contain"
        poster={props.thumbnail?.url}
        preload="metadata"
      />
      <Pressable
        className="absolute inset-0 z-10 rounded-xl h-full bg-black/0 hover:bg-black/40 group transition-all duration-300"
        onPress={onPlayPause}
      >
        <Box className="relative flex-1">
          <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {status === "loading" && isPlaying ? (
              <Spinner size="large" className="text-black" />
            ) : (
              <Icon
                as={isPlaying ? Pause : Play}
                className={`${isPlaying ? "opacity-0" : "opacity-100"} group-hover:opacity-100 transition-opacity duration-300 fill-white stroke-none`}
                size="3xl"
              />
            )}
          </Box>
        </Box>
      </Pressable>
      <Box className="absolute z-20 bottom-0 mx-auto left-1/2 -translate-x-1/2 w-full group/progress pt-2">
        <Progress
          value={duration > 0 ? currentTime : 0}
          max={duration}
          size="xs"
          className="bg-transparent h-1 relative group-hover/progress:h-1.5 transition-all duration-200"
          action="brand"
        >
          <ProgressFilledTrack />
          <Box
            className="absolute h-2 w-2 opacity-0 rounded-full bg-brand-500 z-20 top-1/2 -translate-y-1/2 -translate-x-1/2 group-hover/progress:opacity-100 transition-all duration-200"
            style={{ left: `${(currentTime / duration) * 100}%` }}
          ></Box>
        </Progress>
      </Box>
    </>
  );
};

export default VideoContent;
